/* eslint-disable no-nested-ternary */
import React, { useRef } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSysTheme, useDisplayTheme, useDashboardViewLayout } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { Theme } from 'features/ui/types'
import {
  DoubleChevronUp as DoubleChevronUpIcon,
  Pin as PinIcon,
  Tooltip,
  Unpin as UnpinIcon
  // @ts-ignore
} from '@netbasequid/core'
// @ts-ignore
import { colorUtils } from '@netbasequid/theme'
import { NavBar } from 'components/wrapper' // * maybe change import path
import ScreenSpinner from 'components/share/ScreenSpinner'
import { setPinMode, setIntroductionToolTips, setIsFilterDropdownOpen } from 'features/ui/uiSlice'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  selectIsDashboardCopying,
  selectIsDashboardDeleting,
  selectDashboardInfoCategory
} from 'features/dashboardInfo/selectors'
import { selectHasIntroductionToolTips } from 'features/ui/selectors'
import { heapTrack, HeapEvent } from 'utils/heapUtils'
import { ScrollbarStyle } from 'styles'

import SpinningQ from 'components/share/SpinningQ'
import SidePanel from './SidePanel'
import ActionList from './ActionList'
import { useRetrievePreRequiredRequests, usePresentationModeUrlHandler } from './hooks'
import DashboardMain from '../main'
import { SidePannelIntroductionTooltip } from './introductionTooltip'

const layouts = {
  pinButtonWidth: 34,
  navBarHeight: 48,
  actionListHeight: 56,
  shrinkSideBarWidth: 48,
  expandSideBarWidth: 385,
  shrinkSideBarWordingWidth: 400,
  shrinkSideBarPaddingTop: 22,
  shrinkSideBarPaddingX: 12
}

const pinButtonBg = ({
  $pinMode,
  $theme,
  $mode
}: {
  $pinMode: boolean
  $theme: SystemTheme
  $mode: Theme
}) => $theme.elevation[$pinMode ? 0 : $mode === 'light' ? 100 : 200].background

const PinButtonContainer = styled.div<{
  $pinMode: boolean
  $theme: SystemTheme
  $mode: Theme
}>`
  position: absolute;
  border: 1px solid ${({ $theme }) => $theme.common.divider[100]};
  border-radius: 0 2px 2px 0;
  box-shadow: 2px 1.2px 3px 0px rgba(0, 0, 0, 0.07);
  box-sizing: border-box;
  border-left: 0;
  width: ${layouts.pinButtonWidth}px;
  height: ${layouts.pinButtonWidth}px;
  right: ${-layouts.pinButtonWidth}px;
  top: 11px;
  display: none;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  background-color: ${pinButtonBg};
  &:hover {
    background-color: ${({ $pinMode, $theme, $mode }) =>
      colorUtils.convertRGBAToHex(
        $theme.interaction.onElevation.default,
        colorUtils.convertHexToRGBA(pinButtonBg({ $pinMode, $theme, $mode }))
      )};
  }
  &:active {
    background-color: ${({ $pinMode, $theme, $mode }) =>
      colorUtils.convertRGBAToHex(
        $theme.interaction.onElevation.pressed,
        colorUtils.convertHexToRGBA(pinButtonBg({ $pinMode, $theme, $mode }))
      )};
  }
`

const SidePannelContent = styled.div<{
  $pinMode: boolean
  $theme: SystemTheme
  $mode: Theme
}>`
  background-color: ${({ $theme, $mode }) =>
    $theme.elevation[$mode === 'light' ? 100 : 200].background};
  height: 100%;
  width: ${layouts.expandSideBarWidth}px;
  transition: transform 0.3s ease-out;
  transform: translateX(${-(layouts.expandSideBarWidth + layouts.pinButtonWidth)}px);

  border-right: 1px solid ${({ $theme }) => $theme.common.divider[100]};
  box-shadow: ${({ $theme }) => $theme.elevation[200].boxShadow};
  box-sizing: border-box;

  &:hover > ${PinButtonContainer} {
    display: flex;
  }

  ${({ $pinMode, $theme }) =>
    $pinMode &&
    css`
      transform: translateX(0px);
      background-color: ${$theme.elevation[0].background};
      box-shadow: none;
    `};
`

const DashboardViewContainer = styled.div<{
  $presentationMode: boolean
  $pinMode: boolean
  $theme: SystemTheme
  $mode: Theme
}>`
  display: grid;
  grid-template-columns:
    ${layouts.shrinkSideBarWidth}px calc(
      ${layouts.expandSideBarWidth}px - ${layouts.shrinkSideBarWidth}px
    )
    1fr;
  grid-template-rows: ${layouts.navBarHeight}px ${layouts.actionListHeight}px 1fr;

  height: 100vh;
  min-width: 1280px;
  background-color: ${({ $theme }) => $theme.elevation[0].background};

  transition: 0.3s ease-out;
  ${({ $presentationMode }) =>
    $presentationMode &&
    css`
      grid-template-columns: 0px 0 1fr;
      grid-template-rows: 0px 0px 1fr;
    `};

  & > .nav-header {
    background-color: ${({ $theme }) => $theme.elevation[0].background};

    position: relative;
    z-index: 2015;
    box-shadow: ${({ $theme }) => $theme.elevation[100].boxShadow};

    grid-column: 1 / 4;
    grid-row: 1 / 2;

    transform: translateY(0px);
    transition: transform 0.3s ease-out;

    ${({ $presentationMode }) =>
      $presentationMode &&
      css`
        transform: translateY(${-layouts.navBarHeight}px);
      `};
  }
  & > .action-list {
    background-color: ${({ $theme }) => $theme.elevation[100].background};
    grid-column: ${({ $pinMode }) => ($pinMode ? '3 / 4' : '2 / 4')};
    grid-row: 2 / 3;
    overflow: hidden; // * for long dashboard name

    transform: translateY(0px);
    transition: transform 0.3s ease-out;
    ${({ $presentationMode }) =>
      $presentationMode &&
      css`
        transform: translateY(${-(layouts.navBarHeight + layouts.actionListHeight)}px);
      `};
  }
  & > .side-pannel {
    border-right: 1px solid ${({ $theme }) => $theme.common.divider[100]};
    color: white;
    background-color: ${({ $theme }) => $theme.elevation[0].background};
    z-index: 2014;

    grid-column: ${({ $pinMode }) => ($pinMode ? '1 / 3' : '1 / 2')};
    grid-row: 2 / 4;

    transform: translateX(0px);
    transition: transform 0.3s ease-out;
    ${({ $presentationMode, $pinMode }) =>
      $presentationMode &&
      css`
        transform: ${$pinMode
          ? `translateX(-${layouts.expandSideBarWidth}px)`
          : `translateX(-${layouts.shrinkSideBarWidth}px)`};
      `};

    &:hover > ${SidePannelContent} {
      transform: translateX(0px);
    }
    & > .side-pannel-label {
      color: ${({ $theme }) => $theme.text.primary};
      width: ${layouts.shrinkSideBarWordingWidth}px;
      height: ${({ $theme }) => $theme.typescale[16].lineHeight};
      line-height: ${({ $theme }) => $theme.typescale[16].lineHeight};
      font-size: ${({ $theme }) => $theme.typescale[16].size};
      font-family: var(--large-font-family);
      font-weight: ${({ $theme }) => $theme.fontWeight.semibold};

      position: absolute;
      transform: rotate(90deg);
      top: calc(
        ${layouts.shrinkSideBarWordingWidth / 2}px - ${layouts.shrinkSideBarPaddingX}px +
          ${layouts.shrinkSideBarPaddingTop}px
      );
      left: calc(
        -${layouts.shrinkSideBarWordingWidth / 2}px + ${layouts.shrinkSideBarPaddingX * 2}px
      );

      display: flex;
      align-items: center;
      gap: 12px;
    }
  }
  & > .dashboard {
    grid-column: ${({ $pinMode }) => ($pinMode ? '3 / 4' : '2 / 4')};
    grid-row: 3 / 4;

    background-color: ${({ $theme }) => $theme.elevation[0].background};
    overflow: auto;
    ${ScrollbarStyle}
  }
`

const LoadingMask = styled.div<{ $theme: SystemTheme }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  z-index: 9999;
  color: white;
  font-size: 14px;
  width: 100vw;
  height: 100vh;
`

const DashboardView = () => {
  const { pinMode, presentationMode } = useDashboardViewLayout()
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const dispatch = useAppDispatch()
  const dasboardRef = useRef<{
    dashboardRef: HTMLDivElement
    exportPDF: () => void
  } | null>(null)
  const currentCategory = useAppSelector(selectDashboardInfoCategory)
  const { hasSidePanelTooltip } = useAppSelector(selectHasIntroductionToolTips)
  const { t } = useTranslation()

  const { pageConfigStatus } = useRetrievePreRequiredRequests()
  usePresentationModeUrlHandler()

  const isCopying = useAppSelector(selectIsDashboardCopying)
  const isDeleting = useAppSelector(selectIsDashboardDeleting)

  const handlePdfExport = async () => {
    heapTrack(HeapEvent.V4DashboardPdfExportClick)
    await dasboardRef.current?.exportPDF()
  }

  const handlePinToggle = () => {
    const nextPinMode = !pinMode
    dispatch(setPinMode(nextPinMode))
    heapTrack(HeapEvent.V4DashboardListPinToggle, { isPinned: nextPinMode })
  }

  const handleMouseEnter = () => {
    if (hasSidePanelTooltip) {
      dispatch(setIntroductionToolTips({ sidePanelIsRead: true }))
    }
  }

  const handleMouseLeave = () => {
    dispatch(setIsFilterDropdownOpen(false))
  }

  if (pageConfigStatus !== 'succeeded') return <ScreenSpinner />
  return (
    <>
      {isCopying && (
        <LoadingMask $theme={theme}>
          <SpinningQ size={24} color="white" />
          {t('duplicatingDashboardThreeDot')}
        </LoadingMask>
      )}
      {isDeleting && (
        <LoadingMask $theme={theme}>
          <SpinningQ size={24} color="white" />
          {t('deletingDashboardThreeDot')}
        </LoadingMask>
      )}
      <DashboardViewContainer
        className="dashboard-view-layout"
        $presentationMode={presentationMode}
        $mode={mode}
        $pinMode={pinMode}
        $theme={theme}>
        <div className="nav-header">
          <NavBar />
        </div>
        <div className="action-list">
          <ActionList onPdfExport={handlePdfExport} />
        </div>
        {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
        <div
          className="side-pannel"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <div className="side-pannel-label">
            <span>{t(currentCategory)}</span>
            <DoubleChevronUpIcon color={theme.text.primary} />
          </div>
          <SidePannelContent $pinMode={pinMode} $theme={theme} $mode={mode}>
            <SidePanel />
            <Tooltip
              mode={mode}
              content={pinMode ? t('unpinSidePanel') : t('pinSidePanel')}
              placement="right"
              followCursor={false}
              offset={[0, 2]}>
              <PinButtonContainer
                $pinMode={pinMode}
                $theme={theme}
                $mode={mode}
                onClick={handlePinToggle}>
                {pinMode ? (
                  <UnpinIcon color={theme.text.placeholder} />
                ) : (
                  <PinIcon color={theme.text.placeholder} />
                )}
              </PinButtonContainer>
            </Tooltip>
          </SidePannelContent>
        </div>
        <div className="dashboard">
          <DashboardMain ref={dasboardRef} />
        </div>
        {hasSidePanelTooltip && <SidePannelIntroductionTooltip />}
      </DashboardViewContainer>
    </>
  )
}

export default DashboardView
